const LOW_POWER_KEY = 'britbank.lowPowerMode';

export const LOW_POWER_FPS = 31;
export const FULL_POWER_FPS = 60;

export function liveFrameRate(lowPower: boolean): number {
  return lowPower ? LOW_POWER_FPS : FULL_POWER_FPS;
}

function windowOf(doc: Document): Window & typeof globalThis {
  return (doc.defaultView ?? window) as Window & typeof globalThis;
}

function deviceDefault(doc: Document): boolean {
  const owner = windowOf(doc);
  return (
    owner.matchMedia?.('(pointer: coarse)').matches === true ||
    (owner.navigator.maxTouchPoints ?? 0) > 0
  );
}

export interface LowPowerModeOptions {
  onChange?: (enabled: boolean) => void;
  doc?: Document;
  /** Overrides device detection for tests and embedded clients. */
  defaultEnabled?: boolean;
}

export interface LowPowerModeHandle {
  isEnabled(): boolean;
  setEnabled(enabled: boolean): void;
}

export function initLowPowerMode(opts: LowPowerModeOptions = {}): LowPowerModeHandle {
  const doc = opts.doc ?? document;
  const checkbox = doc.getElementById('low-power-checkbox') as HTMLInputElement | null;
  const fallback = opts.defaultEnabled ?? deviceDefault(doc);
  let enabled = fallback;
  try {
    const saved = windowOf(doc).localStorage.getItem(LOW_POWER_KEY);
    if (saved === '1') enabled = true;
    else if (saved === '0') enabled = false;
  } catch {
    // Storage can be unavailable in private browsing. The device default still works.
  }

  const apply = (next: boolean, persist: boolean): void => {
    enabled = next;
    if (checkbox) checkbox.checked = enabled;
    if (persist) {
      try {
        windowOf(doc).localStorage.setItem(LOW_POWER_KEY, enabled ? '1' : '0');
      } catch {
        // Keep the session choice when storage is unavailable.
      }
    }
    opts.onChange?.(enabled);
  };

  checkbox?.addEventListener('change', () => apply(checkbox.checked, true));
  apply(enabled, false);

  return {
    isEnabled: () => enabled,
    setEnabled: (next) => apply(next, true),
  };
}
